// ============================================================
// THE UNSTABLE PIERRE
// A cosmic boardwalk pier floating over an ocean of void and stars.
// The hub that connects all games in the Illegal Empire universe.
//
// Map: 56×20 tiles — the main spine runs east-west,
// district wings branch north and south into the void.
//
// Districts (current):
//   Terror Wing   — dark gothic, card table, candles, fog
//   Casino Wing   — neon gold, dice on the floor, felt carpet
//   Sol Wing      — celestial ruin, cracked stone, crystals
//   DTD Wing      — dim lanterns, scratched railings, dread
//   [???] Far End — always foggy, always locked, always growing
//
// Willy is here. He owns this place apparently.
// The sign outside has a typo. Nobody mention it.
// ============================================================

import { useState, useEffect, useRef, useCallback } from "react";
import { ref, set, onValue, remove, get, update } from "firebase/database";
import { db } from "./firebase";
import { SD, shimmerBorder } from "./theme";
import { listenToProfile } from "./progression"; // awardCrests removed 2026-08-18 — imported here, never called
import { useOrientationLock, RotateGate } from "./engineIndex";

// ─────────────────────────────────────────────────────────────
// CDN
// ─────────────────────────────────────────────────────────────
const CDN = "https://terror-assets.pages.dev";

// ─────────────────────────────────────────────────────────────
// MAP DIMENSIONS — 56 wide × 20 tall
// Plaza was 34×20. This is ~3.3x bigger.
// ─────────────────────────────────────────────────────────────
const COLS = 56;
const ROWS = 20;

// ⚠ THE POINTER LAW (2026-08-22) — TOUCH ALONE IS NOT "MOBILE".
// `navigator.maxTouchPoints > 0` is true on every touchscreen laptop and
// 2-in-1, so this said "phone" on caP'n's PC. In OrientationLock it was a
// hard lockout (the rotate curtain over a monitor that cannot rotate);
// here it is thumb controls and phone layout handed to someone with a
// mouse. `(pointer: coarse)` asks about the PRIMARY pointer instead of
// what the hardware merely supports: phone/tablet coarse, touchscreen PC
// fine. Touch is still required, so mouse-only desktops are unchanged.
// Full reasoning lives once, on isMobile() in OrientationLock.js.
const isMobile = () => {
  if (typeof window === "undefined") return false;
  if (!(("ontouchstart" in window) || (navigator.maxTouchPoints || 0) > 0)) return false;
  try {
    if (typeof window.matchMedia === "function") return window.matchMedia("(pointer: coarse)").matches;
  } catch { /* fall back to the touch-only answer rather than throw */ }
  return true;
};
const isPortrait = () => isMobile() && window.innerHeight > window.innerWidth;

function calcTile() {
  if (isPortrait()) return 40;
  return Math.max(
    Math.ceil(window.innerWidth  / COLS),
    Math.ceil(window.innerHeight / ROWS)
  );
}

function calcCamOffset(px, py, tile) {
  const vw   = window.innerWidth;
  const vh   = window.innerHeight;
  const mapW = COLS * tile;
  const mapH = ROWS * tile;
  const rawX = px * tile + tile / 2 - vw / 2;
  const rawY = py * tile + tile / 2 - vh / 2;
  return {
    x: Math.max(0, Math.min(mapW - vw, rawX)),
    y: Math.max(0, Math.min(mapH - vh, rawY)),
  };
}

// ─────────────────────────────────────────────────────────────
// TILE TYPES
// ─────────────────────────────────────────────────────────────
const T = { FLOOR: 0, WALL: 1, VOID: 2, OBJECT: 3, NPC: 4 };

// ─────────────────────────────────────────────────────────────
// GAME PORTALS — interactable objects that launch games
// type: "internal" = setScreen() | "external" = window.open (future)
// ─────────────────────────────────────────────────────────────
const GAME_PORTALS = [
  {
    id: "terror",
    label: "Terror TCG",
    flavor: "The card game at the edge of everything.",
    object: "card_table",     // art asset key
    icon: "🃏",
    color: "#7c3aed",
    verb: "◆ Play",
    type: "internal",
    screen: "plaza",          // App.js screen to navigate to
    x: 10, y: 5,             // Terror Wing — north branch
    rx: 3, ry: 2,
  },
  {
    id: "casino",
    label: "The Casino",
    flavor: "Luck is a currency. Spend wisely.",
    object: "dice",
    icon: "🎲",
    color: "#f59e0b",
    verb: "◆ Roll",
    type: "coming_soon",
    x: 20, y: 15,            // Casino Wing — south branch
    rx: 3, ry: 2,
  },
  {
    id: "sol",
    label: "Sol Survivor",
    flavor: "The ruins remember what the stars forgot.",
    object: "crystal",
    icon: "✦",
    color: "#22d3ee",
    verb: "◆ Enter",
    type: "coming_soon",
    x: 34, y: 5,             // Sol Wing — north branch
    rx: 3, ry: 2,
  },
  {
    id: "dtd",
    label: "Dusk till Dawn",
    flavor: "Don't let the lantern go out.",
    object: "lantern",
    icon: "🕯️",
    color: "#f87171",
    verb: "◆ Wander",
    type: "coming_soon",
    x: 46, y: 5,             // DTD Wing — north branch
    rx: 3, ry: 2,
  },
];

// ─────────────────────────────────────────────────────────────
// INTERACTABLES — NPCs and objects on the pier
// ─────────────────────────────────────────────────────────────
const INTERACTABLES = [
  {
    id: "pierre",             // Willy. He's Pierre now. Don't ask.
    label: "Pierre",
    verb: "◆ Speak",
    color: SD.purpleLight,
    x: 5, y: 10,
    rx: 3, ry: 2,
  },
  {
    id: "crest_well",
    label: "Crest Well",
    verb: "◆ Offer",
    color: SD.gold,
    x: 28, y: 10,             // Center of the pier
    rx: 3, ry: 2,
  },
  {
    id: "mystery",
    label: "???",
    verb: "◆ ???",
    color: "#475569",
    x: 52, y: 10,             // Far end — always foggy
    rx: 2, ry: 2,
    locked: true,
  },
];

// Spawn near the entrance, just past Pierre
const SPAWN = { x: 8, y: 10 };

// ─────────────────────────────────────────────────────────────
// MAP BUILDER
// The pier is walkable planks. Void tiles surround the wings.
// Districts are raised areas branching off the main spine.
// ─────────────────────────────────────────────────────────────
function buildMap() {
  const map = Array.from({ length: ROWS }, () => Array(COLS).fill(T.VOID));

  // ── Main boardwalk spine — rows 9-11, full width ──────────
  for (let r = 8; r <= 12; r++)
    for (let c = 0; c < COLS; c++)
      map[r][c] = T.FLOOR;

  // ── Terror Wing (north, cols 6-16, rows 3-8) ─────────────
  for (let r = 3; r <= 8; r++)
    for (let c = 6; c <= 16; c++)
      map[r][c] = T.FLOOR;

  // ── Casino Wing (south, cols 16-26, rows 13-17) ───────────
  for (let r = 13; r <= 17; r++)
    for (let c = 16; c <= 26; c++)
      map[r][c] = T.FLOOR;

  // ── Sol Wing (north, cols 28-40, rows 3-8) ────────────────
  for (let r = 3; r <= 8; r++)
    for (let c = 28; c <= 40; c++)
      map[r][c] = T.FLOOR;

  // ── DTD Wing (north, cols 42-52, rows 3-8) ────────────────
  for (let r = 3; r <= 8; r++)
    for (let c = 42; c <= 52; c++)
      map[r][c] = T.FLOOR;

  // ── Far End mystery platform (cols 49-55, rows 8-12) ──────
  for (let r = 8; r <= 12; r++)
    for (let c = 49; c <= 55; c++)
      map[r][c] = T.FLOOR;

  // ── Mark objects ─────────────────────────────────────────
  GAME_PORTALS.forEach(p => {
    for (let dr = -p.ry; dr <= p.ry; dr++)
      for (let dc = -p.rx; dc <= p.rx; dc++) {
        const row = p.y + dr, col = p.x + dc;
        if (row >= 0 && row < ROWS && col >= 0 && col < COLS && map[row][col] === T.FLOOR)
          map[row][col] = T.OBJECT;
      }
  });
  INTERACTABLES.forEach(n => {
    if (n.y >= 0 && n.y < ROWS && n.x >= 0 && n.x < COLS)
      map[n.y][n.x] = T.NPC;
  });

  return map;
}

const MAP = buildMap();

function isWalkable(x, y) {
  if (x < 0 || y < 0 || x >= COLS || y >= ROWS) return false;
  const t = MAP[y][x];
  return t === T.FLOOR || t === T.OBJECT || t === T.NPC;
}

function getPortalAt(x, y) {
  return GAME_PORTALS.find(p => Math.abs(x - p.x) <= p.rx && Math.abs(y - p.y) <= p.ry) || null;
}

function getInteractableAt(x, y) {
  return INTERACTABLES.find(n => Math.abs(x - n.x) <= n.rx && Math.abs(y - n.y) <= n.ry) || null;
}

// ─────────────────────────────────────────────────────────────
// PIERRE DIALOGUE — Willy in a different hat
// He owns this place. Supposedly. The sign is misspelled.
// ─────────────────────────────────────────────────────────────
function getPierreDialogue(profile) {
  const crests = profile?.divineCrests || 0;
  const wins   = profile?.totalWins || 0;

  if (!profile) return [
    "Hey! HEY. You need a profile to be here.",
    "The void doesn't do refunds.",
  ];
  if (wins === 0) return [
    "Welcome to the... establishment.",
    "I am Pierre. The proprietor. The visionary.",
    "Don't worry about the name on the sign. It's... intentional.",
  ];
  if (crests > 2000) return [
    `${crests.toLocaleString()} Crests. I see you've been busy.`,
    "Spend some. The pier needs maintenance.",
    "...I mean, enjoy the games.",
  ];
  if (wins > 20) return [
    "You've been around, haven't you.",
    "I've watched you walk these planks more than once.",
    "The void beneath us? It's watching too. Just so you know.",
  ];
  return [
    "The pier is... somewhat unstable today.",
    "It's always somewhat unstable. That's part of the charm.",
    "Each wing leads somewhere different. You'll figure it out.",
  ];
}

// ─────────────────────────────────────────────────────────────
// CREST WELL DIALOGUE
// ─────────────────────────────────────────────────────────────
function getCrestWellDialogue(profile) {
  const crests = profile?.divineCrests || 0;
  return [
    `You have ${crests.toLocaleString()} Divine Crests.`,
    "They're good across all of the pier.",
    "Win anywhere. Spend anywhere.",
    "That's the deal.",
  ];
}

// ─────────────────────────────────────────────────────────────
// DISTRICT COLORS — ambient tints per zone
// ─────────────────────────────────────────────────────────────
function getDistrictColor(x, y) {
  // Terror Wing
  if (y <= 8 && x >= 6 && x <= 16) return "rgba(124,58,237,0.08)";
  // Casino Wing
  if (y >= 13 && x >= 16 && x <= 26) return "rgba(245,158,11,0.08)";
  // Sol Wing
  if (y <= 8 && x >= 28 && x <= 40) return "rgba(34,211,238,0.08)";
  // DTD Wing
  if (y <= 8 && x >= 42 && x <= 52) return "rgba(248,113,113,0.06)";
  // Main spine
  return "rgba(139,92,246,0.04)";
}

// ─────────────────────────────────────────────────────────────
// VOID OCEAN RENDERER — the star-field beneath the pier
// ─────────────────────────────────────────────────────────────
function VoidOcean({ tile }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;
    // Stars
    const stars = Array.from({ length: 300 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      r: Math.random() * 1.5 + 0.3,
      a: Math.random(),
      speed: Math.random() * 0.003 + 0.001,
      phase: Math.random() * Math.PI * 2,
    }));
    let frame = 0;
    let raf;
    function draw() {
      ctx.clearRect(0, 0, W, H);
      // Deep void gradient
      const grad = ctx.createLinearGradient(0, 0, W, H);
      grad.addColorStop(0, "#0a0018");
      grad.addColorStop(0.3, "#050012");
      grad.addColorStop(0.6, "#020820");
      grad.addColorStop(1, "#0d0525");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);
      // Nebula wisps
      const t = frame * 0.005;
      [
        { x: W*0.2, y: H*0.4, r: W*0.3, c1: "rgba(124,58,237,0.06)", c2: "transparent" },
        { x: W*0.7, y: H*0.6, r: W*0.25, c1: "rgba(34,211,238,0.05)", c2: "transparent" },
        { x: W*0.5, y: H*0.2, r: W*0.2, c1: "rgba(245,158,11,0.04)", c2: "transparent" },
      ].forEach(n => {
        const g = ctx.createRadialGradient(
          n.x + Math.sin(t)*20, n.y + Math.cos(t)*15, 0,
          n.x, n.y, n.r
        );
        g.addColorStop(0, n.c1);
        g.addColorStop(1, n.c2);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
      });
      // Stars
      stars.forEach(s => {
        const a = s.a * (0.5 + 0.5 * Math.sin(frame * s.speed + s.phase));
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${a})`;
        ctx.fill();
      });
      frame++;
      raf = requestAnimationFrame(draw);
    }
    draw();
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <canvas
      ref={canvasRef}
      width={COLS * tile}
      height={ROWS * tile}
      style={{ position: "absolute", left: 0, top: 0, zIndex: 0, opacity: 1 }}
    />
  );
}

// ─────────────────────────────────────────────────────────────
// PIER PLANK RENDERER — the walkable floor tiles
// ─────────────────────────────────────────────────────────────
function PierPlanks({ tile }) {
  const TILE = tile;
  const planks = [];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const cell = MAP[r][c];
      if (cell === T.VOID) continue;
      const district = getDistrictColor(c, r);
      // Plank lines every 3 tiles
      const isPlankLine = c % 3 === 0;
      planks.push(
        <div key={`${r}-${c}`} style={{
          position: "absolute",
          left: c * TILE, top: r * TILE,
          width: TILE, height: TILE,
          background: district,
          borderRight: isPlankLine ? "1px solid rgba(0,0,0,0.3)" : "none",
          borderBottom: "1px solid rgba(0,0,0,0.15)",
          boxSizing: "border-box",
          zIndex: 1,
        }} />
      );
    }
  }
  return <>{planks}</>;
}

// ─────────────────────────────────────────────────────────────
// GAME PORTAL VISUAL — the interactable object for each game
// ─────────────────────────────────────────────────────────────
function GamePortalVisual({ portal, tile }) {
  const TILE = tile;
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setFrame(f => (f+1)%120), 50);
    return () => clearInterval(t);
  }, []);
  const pulse = 0.6 + 0.4 * Math.sin(frame * 0.15);
  const glow = Math.round(pulse * 30);

  // District label above the object
  const districtLabel = {
    terror: "TERROR WING",
    casino: "CASINO WING",
    sol:    "SOL WING",
    dtd:    "DTD WING",
  }[portal.id] || "";

  return (
    <div style={{
      position: "absolute",
      left: (portal.x - 1) * TILE,
      top: (portal.y - 2) * TILE,
      width: TILE * 3,
      zIndex: 6,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      pointerEvents: "none",
    }}>
      {/* District label */}
      <div style={{
        fontSize: 7, letterSpacing: 3, color: portal.color,
        fontFamily: SD.fontDisplay, fontWeight: 700,
        textShadow: `0 0 8px ${portal.color}`,
        opacity: 0.7 + 0.3 * pulse,
        marginBottom: 2,
      }}>{districtLabel}</div>

      {/* Object glow */}
      <div style={{
        width: TILE * 2, height: TILE * 2,
        background: `radial-gradient(ellipse at 50% 80%, ${portal.color}${Math.round(pulse*60).toString(16).padStart(2,"0")} 0%, transparent 70%)`,
        borderRadius: "50%",
        boxShadow: `0 0 ${glow}px ${portal.color}66`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: TILE * 0.9,
        filter: portal.type === "coming_soon" ? "grayscale(0.4)" : "none",
      }}>
        {portal.icon}
      </div>

      {/* Coming soon badge */}
      {portal.type === "coming_soon" && (
        <div style={{
          fontSize: 6, letterSpacing: 2, color: SD.starDim,
          fontFamily: SD.fontDisplay, marginTop: 2,
        }}>SOON</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PROXIMITY BUBBLE — floats above anything interactable
// ─────────────────────────────────────────────────────────────
function ProximityBubble({ color, tileX, tileY, tile, verb, onActivate }) {
  const TILE = tile;
  const [bounce, setBounce] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setBounce(b => (b + 1) % 20), 80);
    return () => clearInterval(t);
  }, []);
  const offsetY = Math.sin(bounce * Math.PI / 10) * 3;

  return (
    <div
      onClick={onActivate}
      style={{
        position: "absolute",
        left: tileX * TILE - 20,
        top: tileY * TILE - 36 + offsetY,
        zIndex: 30,
        cursor: "pointer",
        background: "rgba(5,3,20,0.92)",
        border: `1px solid ${color}`,
        borderRadius: 8,
        padding: "3px 10px",
        display: "flex", alignItems: "center", gap: 4,
        boxShadow: `0 0 12px ${color}44`,
        whiteSpace: "nowrap",
        transition: "top 0.08s",
      }}
    >
      <span style={{
        fontSize: 9, fontFamily: SD.fontDisplay, fontWeight: 700,
        color, letterSpacing: 1,
        textShadow: `0 0 8px ${color}`,
      }}>{verb}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DIALOGUE PANEL — visual novel style, same as Plaza
// ─────────────────────────────────────────────────────────────
function DialoguePanel({ lines, color, speaker, onClose }) {
  const [idx, setIdx] = useState(0);
  const advance = () => {
    if (idx < lines.length - 1) setIdx(i => i + 1);
    else onClose();
  };
  return (
    <div
      onClick={advance}
      style={{
        position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)",
        width: "min(520px, 90vw)",
        background: "rgba(5,3,20,0.97)",
        border: `1px solid ${color}`,
        borderRadius: 12,
        padding: "16px 20px",
        zIndex: 200,
        cursor: "pointer",
        boxShadow: `0 0 24px ${color}33`,
      }}
    >
      <div style={{ fontSize: 9, color, letterSpacing: 3, fontFamily: SD.fontDisplay, marginBottom: 8 }}>
        {speaker.toUpperCase()}
      </div>
      <div style={{ fontSize: 13, color: SD.starWhite, fontFamily: "Georgia, serif", lineHeight: 1.7 }}>
        {lines[idx]}
      </div>
      <div style={{ fontSize: 8, color: SD.starDim, textAlign: "right", marginTop: 8, letterSpacing: 1 }}>
        {idx < lines.length - 1 ? "▶ continue" : "▶ close"}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// GAME PORTAL MODAL — shown when approaching a game object
// ─────────────────────────────────────────────────────────────
function GamePortalModal({ portal, profile, onEnter, onClose }) {
  const crests = profile?.divineCrests || 0;
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 200,
    }}>
      <div style={{
        background: "rgba(5,3,20,0.98)",
        border: `1px solid ${portal.color}`,
        borderRadius: 16,
        padding: "32px 40px",
        width: "min(400px, 88vw)",
        textAlign: "center",
        boxShadow: `0 0 40px ${portal.color}33`,
      }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>{portal.icon}</div>
        <div style={{
          fontSize: 18, fontFamily: SD.fontDisplay, fontWeight: 900,
          color: portal.color, letterSpacing: 3,
          textShadow: `0 0 16px ${portal.color}`,
          marginBottom: 6,
        }}>{portal.label.toUpperCase()}</div>
        <div style={{
          fontSize: 11, color: SD.starDim, fontStyle: "italic",
          fontFamily: "Georgia, serif", marginBottom: 20, lineHeight: 1.6,
        }}>{portal.flavor}</div>

        {/* Crest display */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: "8px 16px",
          marginBottom: 20, border: `1px solid ${SD.void3}`,
        }}>
          <span style={{ fontSize: 14 }}>◆</span>
          <span style={{ fontSize: 13, color: SD.gold, fontFamily: SD.fontDisplay, fontWeight: 900 }}>
            {crests.toLocaleString()}
          </span>
          <span style={{ fontSize: 9, color: SD.starDim, letterSpacing: 2 }}>DIVINE CRESTS</span>
        </div>

        {portal.type === "coming_soon" ? (
          <div style={{
            padding: "12px", borderRadius: 8,
            border: `1px solid ${SD.void3}`,
            color: SD.starDim, fontSize: 11, fontFamily: SD.fontDisplay,
            letterSpacing: 2, marginBottom: 16,
          }}>COMING SOON</div>
        ) : (
          <button
            onClick={() => onEnter(portal)}
            style={{
              width: "100%", padding: "12px",
              background: `linear-gradient(135deg, ${portal.color}22, ${portal.color}44)`,
              border: `1px solid ${portal.color}`,
              borderRadius: 8, cursor: "pointer",
              color: portal.color, fontSize: 12,
              fontFamily: SD.fontDisplay, fontWeight: 900,
              letterSpacing: 3,
              boxShadow: `0 0 16px ${portal.color}33`,
              marginBottom: 12,
            }}
          >{portal.verb.toUpperCase()}</button>
        )}
        <button
          onClick={onClose}
          style={{
            width: "100%", padding: "10px",
            background: "transparent",
            border: `1px solid ${SD.void4}`,
            borderRadius: 8, cursor: "pointer",
            color: SD.starDim, fontSize: 10,
            fontFamily: SD.fontDisplay,
          }}
        >← Back to the Pier</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MOBILE D-PAD
// ─────────────────────────────────────────────────────────────
function MobileDpad({ keysRef }) {
  const dirs = [
    { key: "ArrowUp",    label: "▲", col: 2, row: 1 },
    { key: "ArrowLeft",  label: "◀", col: 1, row: 2 },
    { key: "ArrowDown",  label: "▼", col: 2, row: 3 },
    { key: "ArrowRight", label: "▶", col: 3, row: 2 },
  ];
  const press    = k => { keysRef.current[k] = true; };
  const release  = k => { keysRef.current[k] = false; };
  const releaseAll = () => ["ArrowUp","ArrowDown","ArrowLeft","ArrowRight"].forEach(k => { keysRef.current[k] = false; });
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 20, zIndex: 200,
      display: "grid",
      gridTemplateColumns: "48px 48px 48px",
      gridTemplateRows: "48px 48px 48px",
      gap: 5, touchAction: "none",
    }}>
      {dirs.map(({ key, label, col, row }) => (
        <div key={key}
          style={{
            gridColumn: col, gridRow: row,
            width: 48, height: 48,
            background: "rgba(10,5,30,0.85)",
            border: "1px solid rgba(161,130,250,0.4)",
            borderRadius: 10,
            color: "rgba(200,180,255,0.9)",
            fontSize: 18,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
            userSelect: "none", WebkitUserSelect: "none",
            touchAction: "none",
          }}
          onPointerDown={e => { e.preventDefault(); press(key); }}
          onPointerUp={e => { e.preventDefault(); release(key); }}
          onPointerLeave={e => { e.preventDefault(); release(key); }}
          onPointerCancel={e => { e.preventDefault(); releaseAll(); }}
        >{label}</div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CREST HUD — always visible, top-right
// ─────────────────────────────────────────────────────────────
function CrestHUD({ crests }) {
  return (
    <div style={{
      position: "fixed", top: 16, right: 16, zIndex: 150,
      display: "flex", alignItems: "center", gap: 6,
      background: "rgba(5,3,20,0.92)",
      border: `1px solid ${SD.gold}`,
      borderRadius: 20, padding: "5px 14px",
      boxShadow: shimmerBorder(SD.gold),
    }}>
      <span style={{ fontSize: 13 }}>◆</span>
      <span style={{ fontSize: 13, fontWeight: 900, color: SD.gold, fontFamily: SD.fontDisplay }}>
        {crests?.toLocaleString() || 0}
      </span>
      <span style={{ fontSize: 8, color: SD.starDim, letterSpacing: 2 }}>CRESTS</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PLAYER SPRITE (simplified — reuses terror sprites)
// ─────────────────────────────────────────────────────────────
function PlayerVisual({ player, isLocal, tile }) {
  const TILE = tile;
  const size = Math.floor(TILE * 1.5);
  const color = isLocal ? SD.gold : SD.purpleLight;
  return (
    <div style={{
      position: "absolute",
      left: player.x * TILE - size/2 + TILE/2,
      top:  player.y * TILE - size + TILE,
      width: size, height: size,
      zIndex: 10,
      transition: "left 0.1s, top 0.1s",
      display: "flex", flexDirection: "column", alignItems: "center",
    }}>
      <div style={{
        position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)",
        whiteSpace: "nowrap", fontSize: 8,
        color: isLocal ? SD.goldBright : SD.starSilver,
        background: "rgba(0,0,0,0.7)", padding: "1px 5px", borderRadius: 3,
        border: `1px solid ${isLocal ? SD.gold+"66" : SD.void4}`,
        fontFamily: SD.fontDisplay,
      }}>{player.name}</div>
      <div style={{
        width: size, height: size,
        background: `radial-gradient(circle, ${color}44, ${color}11)`,
        border: `2px solid ${color}88`,
        borderRadius: 6,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: size * 0.45,
      }}>🧍</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN — UNSTABLE PIERRE
// ─────────────────────────────────────────────────────────────
export default function UnstablePierre({ playerName, playerUid, profile, onEnterGame, onBack }) {
  // ── Portrait lock (universal IE behaviour) ────────────────
  const { needsRotate } = useOrientationLock();

  // ── State ────────────────────────────────────────────────
  const [tile, setTile]               = useState(calcTile);
  const [pos, setPos]                 = useState(SPAWN);
  const [facing, setFacing]           = useState({ x: 0, y: 1 });
  const [camOffset, setCamOffset]     = useState({ x: 0, y: 0 });
  const [otherPlayers, setOtherPlayers] = useState({});
  const [nearPortal, setNearPortal]   = useState(null);
  const [nearNPC, setNearNPC]         = useState(null);
  const [showPortalModal, setShowPortalModal] = useState(null);
  const [dialogue, setDialogue]       = useState(null);
  const [liveProfile, setLiveProfile] = useState(profile);

  const keysRef    = useRef({});
  const moveRef    = useRef(null);
  const posRef     = useRef(pos);
  posRef.current   = pos;

  // ── Live profile ─────────────────────────────────────────
  useEffect(() => {
    if (!playerUid) return;
    return listenToProfile(playerUid, p => { if (p) setLiveProfile(p); });
  }, [playerUid]);

  // ── Firebase multiplayer presence ────────────────────────
  useEffect(() => {
    if (!playerUid || !playerName) return;
    const myRef = ref(db, `games/pierre/players/${playerUid}`);
    set(myRef, { name: playerName, x: SPAWN.x, y: SPAWN.y, uid: playerUid });
    const unsub = onValue(ref(db, "games/pierre/players"), snap => {
      const all = snap.val() || {};
      const others = {};
      Object.entries(all).forEach(([uid, data]) => {
        if (uid !== playerUid) others[uid] = data;
      });
      setOtherPlayers(others);
    });
    return () => {
      remove(myRef);
      unsub();
    };
  }, [playerUid, playerName]);

  // ── Sync position to Firebase ────────────────────────────
  const syncPos = useCallback((x, y) => {
    if (!playerUid) return;
    update(ref(db, `games/pierre/players/${playerUid}`), { x, y, name: playerName });
  }, [playerUid, playerName]);

  // ── Resize handler ───────────────────────────────────────
  useEffect(() => {
    const onResize = () => setTile(calcTile());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── Camera ───────────────────────────────────────────────
  useEffect(() => {
    setCamOffset(calcCamOffset(pos.x, pos.y, tile));
  }, [pos, tile]);

  // ── Keyboard ─────────────────────────────────────────────
  useEffect(() => {
    const onDown = e => { keysRef.current[e.key] = true; };
    const onUp   = e => { keysRef.current[e.key] = false; };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup",   onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup",   onUp);
    };
  }, []);

  // ── Movement loop ────────────────────────────────────────
  useEffect(() => {
    const menuOpen = !!showPortalModal || !!dialogue;
    if (menuOpen) return;
    let last = 0;
    const SPEED = 160;
    function loop(ts) {
      if (ts - last > SPEED) {
        last = ts;
        const k = keysRef.current;
        let dx = 0, dy = 0;
        if (k["ArrowLeft"]  || k["a"]) dx = -1;
        if (k["ArrowRight"] || k["d"]) dx =  1;
        if (k["ArrowUp"]   || k["w"]) dy = -1;
        if (k["ArrowDown"] || k["s"]) dy =  1;
        if (dx !== 0) dy = 0; // no diagonal
        if (dx !== 0 || dy !== 0) {
          const nx = posRef.current.x + dx;
          const ny = posRef.current.y + dy;
          setFacing({ x: dx || 0, y: dy });
          if (isWalkable(nx, ny)) {
            setPos({ x: nx, y: ny });
            syncPos(nx, ny);
          }
        }
        // Proximity checks
        const p = getPortalAt(posRef.current.x, posRef.current.y);
        const n = getInteractableAt(posRef.current.x, posRef.current.y);
        setNearPortal(p || null);
        setNearNPC((!p && n) ? n : null);
      }
      moveRef.current = requestAnimationFrame(loop);
    }
    moveRef.current = requestAnimationFrame(loop);
    return () => { if (moveRef.current) cancelAnimationFrame(moveRef.current); };
  }, [showPortalModal, dialogue, syncPos]);

  // ── Interact key (E / Space) ─────────────────────────────
  useEffect(() => {
    const onKey = e => {
      if (e.key === "e" || e.key === "E" || e.key === " ") {
        if (nearPortal) { setShowPortalModal(nearPortal); return; }
        if (nearNPC)    { handleNPCInteract(nearNPC); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nearPortal, nearNPC]);

  // ── NPC interact ─────────────────────────────────────────
  function handleNPCInteract(npc) {
    if (npc.locked) {
      setDialogue({ lines: ["...", "...", "Not yet."], color: "#475569", speaker: "???" });
      return;
    }
    if (npc.id === "pierre") {
      setDialogue({ lines: getPierreDialogue(liveProfile), color: SD.purpleLight, speaker: "Pierre" });
    } else if (npc.id === "crest_well") {
      setDialogue({ lines: getCrestWellDialogue(liveProfile), color: SD.gold, speaker: "Crest Well" });
    }
  }

  // ── Portal enter ─────────────────────────────────────────
  function handleEnterPortal(portal) {
    setShowPortalModal(null);
    if (portal.type === "internal" && onEnterGame) {
      onEnterGame(portal);
    }
  }

  return (
    <div style={{
      position: "fixed", inset: 0,
      overflow: "hidden",
      background: "#020010",
      fontFamily: SD.fontDisplay,
    }}>
      {/* ── Map container — scrolls via camera offset ──── */}
      <div style={{
        position: "absolute",
        left: -camOffset.x, top: -camOffset.y,
        width: COLS * tile, height: ROWS * tile,
      }}>
        {/* Void ocean beneath everything */}
        <VoidOcean tile={tile} />

        {/* Pier planks */}
        <PierPlanks tile={tile} />

        {/* Game portal objects */}
        {GAME_PORTALS.map(p => (
          <GamePortalVisual key={p.id} portal={p} tile={tile} />
        ))}

        {/* NPC labels */}
        {INTERACTABLES.filter(n => !n.locked).map(n => (
          <div key={n.id} style={{
            position: "absolute",
            left: n.x * tile - 20,
            top: n.y * tile - 16,
            fontSize: 8, color: n.color,
            fontFamily: SD.fontDisplay,
            letterSpacing: 2,
            textShadow: `0 0 6px ${n.color}`,
            zIndex: 6,
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}>{n.label.toUpperCase()}</div>
        ))}

        {/* Other players */}
        {Object.entries(otherPlayers).map(([uid, p]) => (
          <PlayerVisual key={uid} player={p} isLocal={false} tile={tile} />
        ))}

        {/* Local player */}
        <PlayerVisual player={{ ...pos, name: playerName }} isLocal tile={tile} />

        {/* Proximity bubbles */}
        {nearPortal && !showPortalModal && (
          <ProximityBubble
            color={nearPortal.color}
            tileX={nearPortal.x}
            tileY={nearPortal.y}
            tile={tile}
            verb={nearPortal.verb}
            onActivate={() => setShowPortalModal(nearPortal)}
          />
        )}
        {nearNPC && !nearPortal && !dialogue && (
          <ProximityBubble
            color={nearNPC.color}
            tileX={nearNPC.x}
            tileY={nearNPC.y}
            tile={tile}
            verb={nearNPC.verb}
            onActivate={() => handleNPCInteract(nearNPC)}
          />
        )}
      </div>

      {/* ── Fixed HUD ───────────────────────────────────── */}
      <CrestHUD crests={liveProfile?.divineCrests} />

      {/* Back button */}
      <button
        onClick={onBack}
        style={{
          position: "fixed", top: 16, left: 16, zIndex: 150,
          background: "rgba(5,3,20,0.9)",
          border: `1px solid ${SD.void3}`,
          borderRadius: 8, padding: "6px 14px",
          color: SD.starDim, cursor: "pointer",
          fontSize: 10, fontFamily: SD.fontDisplay, letterSpacing: 2,
        }}
      >← PLAZA</button>

      {/* Pier title — subtle watermark */}
      <div style={{
        position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)",
        fontSize: 8, color: SD.void3, letterSpacing: 4,
        fontFamily: SD.fontDisplay, zIndex: 100, pointerEvents: "none",
      }}>THE UNSTABLE PIERRE</div>

      {/* Mobile D-Pad */}
      {isMobile() && <MobileDpad keysRef={keysRef} />}

      {/* ── Modals ──────────────────────────────────────── */}
      {showPortalModal && (
        <GamePortalModal
          portal={showPortalModal}
          profile={liveProfile}
          onEnter={handleEnterPortal}
          onClose={() => setShowPortalModal(null)}
        />
      )}

      {dialogue && (
        <DialoguePanel
          lines={dialogue.lines}
          color={dialogue.color}
          speaker={dialogue.speaker}
          onClose={() => setDialogue(null)}
        />
      )}

      {/* ── PORTRAIT LOCK CURTAIN ── universal IE behaviour. */}
      <RotateGate show={needsRotate} />
    </div>
  );
}
